import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabaseClient'
import { mutate } from '../lib/dbMutate'
import { X, UserPlus, Search, Check } from 'lucide-react'

const C = {
  display: { fontFamily: "'VT323', monospace", letterSpacing: '0.08em', textTransform: 'uppercase' },
  mono: { fontFamily: "'JetBrains Mono', monospace" },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '16px' },
  modal: { background: '#0a140a', border: '1px solid #00ff66', boxShadow: '0 0 20px rgba(0,255,102,.2), inset 0 0 30px rgba(0,255,102,.04)', width: '100%', maxWidth: '420px', padding: '24px', maxHeight: '90vh', overflowY: 'auto', position: 'relative', color: '#00ff66', fontFamily: "'JetBrains Mono', monospace", textShadow: '0 0 4px rgba(0,255,102,.5)' },
  input: { width: '100%', background: 'rgba(0,20,8,.6)', color: '#00ff66', border: '1px solid rgba(0,255,102,.3)', padding: '10px 14px', paddingLeft: '36px', fontFamily: "'JetBrains Mono', monospace", fontSize: '13px', outline: 'none', textShadow: '0 0 4px rgba(0,255,102,.3)' },
  btnPrimary: { width: '100%', fontFamily: "'VT323', monospace", fontSize: '16px', letterSpacing: '0.1em', textTransform: 'uppercase', background: '#00ff66', color: '#000', border: 'none', padding: '12px 20px', cursor: 'pointer', boxShadow: '0 0 8px #00ff66', textShadow: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' },
}

const roleTag = (role) => {
  switch(role) {
    case 'm55_admin': return { background: 'rgba(154,102,255,.12)', color: '#b380ff', border: '1px solid rgba(154,102,255,.3)' }
    case 'gm': return { background: 'rgba(255,204,51,.08)', color: '#ffcc33', border: '1px solid rgba(255,204,51,.3)' }
    default: return { background: 'rgba(0,255,102,.06)', color: '#4aaa5a', border: '1px solid rgba(0,255,102,.15)' }
  }
}
const roleLabel = (r) => { switch(r) { case 'm55_admin': return 'ADMIN'; case 'gm': return 'GM'; case 'co_gm': return 'CO-GM'; case 'player': return 'PLAYER'; default: return r?.toUpperCase() } }

// factions: optional array of { id, name, color }. When provided (live-event
// direct-add), a faction picker is shown and required before submit.
export default function AddPlayer({ session, onClose, onAdded, factions = [] }) {
  const [users, setUsers] = useState([])
  const [filtered, setFiltered] = useState([])
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState(new Set())
  const [selectedFaction, setSelectedFaction] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [existingPlayers, setExistingPlayers] = useState([])

  useEffect(() => { fetchUsers(); fetchExistingPlayers() }, [])
  useEffect(() => {
    const available = users.filter(u => !existingPlayers.includes(u.id))
    if (!search.trim()) { setFiltered(available); return }
    const q = search.toLowerCase()
    setFiltered(available.filter(u => u.display_name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.callsign?.toLowerCase().includes(q)))
  }, [search, users, existingPlayers])

  const fetchUsers = async () => { const { data } = await supabase.from('users').select('id, display_name, email, callsign, role').order('display_name'); setUsers(data || []); setFiltered(data || []) }
  const fetchExistingPlayers = async () => { const { data } = await supabase.from('event_players').select('user_id').eq('event_id', session.id); setExistingPlayers(data?.map(p => p.user_id) || []) }
  const toggleSelect = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })
  // TD-3: GM / Co-GM are no longer assignable from the roster add modal.
  // Org-side GMs come from org_members.org_role='org_gm' (auto-materialised
  // into event_players via Hierarchy Step 9 + the org-promotion backfill).
  // Co-GMs are assigned via event setup. Keeping toggleGm + gmIds removed.

  // Faction assignment is no longer set on add — it's inferred when the
  // player is placed into an MCS node (the node-member trigger cascades
  // node.faction_id back to event_players via app code, or the player
  // takes the branch's locked faction). Keeps add-player single-purpose.
  const requireFaction = factions.length > 0
  const canSubmit = selected.size > 0 && (!requireFaction || !!selectedFaction)

  const handleAdd = async () => {
    if (!canSubmit) return; setLoading(true); setMessage('')
    const inserts = [...selected].map(uid => ({
      event_id: session.id,
      user_id: uid,
      event_role: 'player',
      reg_status: 'approved',
      session_points: 0,
      ...(requireFaction && selectedFaction ? { faction_id: selectedFaction } : {}),
    }))
    // Track 1.2 — mutate() auto-busts event_players + event_factions +
    // events caches so the GM's roster + faction tiles update without
    // hard refresh. Track 5.2 — mutate() also attaches a
    // friendlyMessage translation of the Postgres error.
    const result = await mutate('event_players', () =>
      supabase.from('event_players').insert(inserts)
    )
    if (result.error) {
      setMessage(result.friendlyMessage || 'Failed to add players.')
      setLoading(false)
    } else {
      onAdded()
    }
  }

  return (
    <div style={C.overlay}>
      <div style={C.modal}>
        <div style={{ position:'absolute',top:-1,left:-1,width:10,height:10,borderTop:'2px solid #9aff9a',borderLeft:'2px solid #9aff9a' }}/>
        <div style={{ position:'absolute',bottom:-1,right:-1,width:10,height:10,borderBottom:'2px solid #9aff9a',borderRight:'2px solid #9aff9a' }}/>

        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-2">
            <UserPlus size={20} style={{ color: '#00ff66' }} />
            <h2 style={{ ...C.display, fontSize: '20px', margin: 0 }}>ADD PLAYERS</h2>
          </div>
          {/* Track 6.2 — 44x44 min hit area on the modal close. */}
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#4aaa5a', cursor: 'pointer', minHeight: 44, minWidth: 44, padding: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><X size={20} /></button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ position: 'relative' }}>
            <Search size={16} style={{ position: 'absolute', left: 12, top: 12, color: '#4aaa5a' }} />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, callsign, email..." maxLength={100} style={C.input} />
          </div>

          <div style={{ maxHeight: 208, overflowY: 'auto' }} className="modal-scroll">
            {filtered.length === 0 ? (
              <p className="text-center py-4" style={{ color: '#2a6a3a', fontSize: 12 }}>No players found</p>
            ) : filtered.map(u => {
              const isSel = selected.has(u.id)
              const rt = roleTag(u.role)
              return (
                <div key={u.id} style={{ marginBottom: 2 }}>
                  <div onClick={() => toggleSelect(u.id)} className="flex items-center justify-between" style={{ padding: '10px 14px', cursor: 'pointer', background: isSel ? '#00ff66' : 'rgba(0,20,8,.3)', color: isSel ? '#000' : '#00ff66', textShadow: isSel ? 'none' : '0 0 4px rgba(0,255,102,.5)', borderBottom: isSel ? 'none' : '1px solid rgba(0,255,102,.06)' }}>
                    <div className="flex items-center gap-3">
                      <div style={{ width: 18, height: 18, border: isSel ? '2px solid #000' : '2px solid #4aaa5a', display: 'flex', alignItems: 'center', justifyContent: 'center', background: isSel ? '#000' : 'none' }}>
                        {isSel && <Check size={12} style={{ color: '#00ff66' }} />}
                      </div>
                      <div>
                        <span style={{ fontWeight: 500 }}>{u.display_name || u.email}</span>
                        {u.callsign && <span style={{ marginLeft: 8, color: isSel ? '#0a140a' : '#4aaa5a', fontSize: 12 }}>"{u.callsign}"</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span style={{ fontSize: 10, padding: '2px 6px', ...rt, textShadow: 'none' }}>{roleLabel(u.role)}</span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {requireFaction && (
            <div>
              <label style={{ ...C.mono, fontSize: 10, color: '#4aaa5a', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 6, display: 'block' }}>
                Assign Faction <span style={{ color: '#ff2a2a' }}>*</span>
              </label>
              <select
                value={selectedFaction}
                onChange={e => setSelectedFaction(e.target.value)}
                style={{ width: '100%', background: 'rgba(0,20,8,.6)', color: '#00ff66', border: '1px solid rgba(0,255,102,.3)', padding: '8px 12px', fontFamily: "'JetBrains Mono', monospace", fontSize: 13, outline: 'none' }}
              >
                <option value="">— Select faction —</option>
                {factions.map(f => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>
          )}

          {message && <p style={{ color: '#ff2a2a', fontSize: 12 }}>{message}</p>}

          <button onClick={handleAdd} disabled={loading || !canSubmit} style={{ ...C.btnPrimary, opacity: loading || !canSubmit ? 0.5 : 1 }}>
            <UserPlus size={16} /> {loading ? 'ADDING...' : selected.size === 0 ? 'SELECT PLAYERS' : requireFaction && !selectedFaction ? 'SELECT FACTION' : `ADD ${selected.size} PLAYER${selected.size > 1 ? 'S' : ''}`}
          </button>
        </div>
      </div>
    </div>
  )
}
